import React, { useEffect, useRef, useState } from 'react';
import { can } from '../utils/permissions';
import api from '../services/ApiService';
import logo from '../assets/logo.png';
import liveEvents from '../services/LiveEvents';
import sipService from '../services/SipService';

import CrmSidePanel from './CrmSidePanel';
import QueueAIPanel from './QueueAIPanel';




// Request browser notification permission so incoming calls ring in background
function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

let _incomingNotif = null;
function showIncomingCallNotification(from, queueName) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible') return; // already visible, no need
  try {
    if (_incomingNotif) { _incomingNotif.close(); }
    const title = queueName ? `Incoming call from ${queueName}` : 'Incoming Call';
    _incomingNotif = new Notification(title, {
      body: `Caller: ${from}\nClick to answer`,
      icon: '/favicon.ico',
      tag: 'incoming-call',
      requireInteraction: true,
      silent: false,
    });
    _incomingNotif.onclick = () => {
      window.focus();
      if (_incomingNotif) { _incomingNotif.close(); _incomingNotif = null; }
    };
  } catch (e) {}
}
function clearIncomingCallNotification() {
  if (_incomingNotif) { _incomingNotif.close(); _incomingNotif = null; }
}

const KEYPAD = [
  { d: '1', sub: '' },    { d: '2', sub: 'ABC' },  { d: '3', sub: 'DEF' },
  { d: '4', sub: 'GHI' }, { d: '5', sub: 'JKL' },  { d: '6', sub: 'MNO' },
  { d: '7', sub: 'PQRS' },{ d: '8', sub: 'TUV' },  { d: '9', sub: 'WXYZ' },
  { d: '*', sub: '' },    { d: '0', sub: '+' },     { d: '#', sub: '' },
];

const AVATAR_COLORS = ['#7C3AED','#0EA5E9','#059669','#F59E0B','#EF4444','#8B5CF6','#D946EF','#0891B2'];
const avatarBg = (idx) => AVATAR_COLORS[idx % AVATAR_COLORS.length];

const SipLabel = { registered:'Registered','pbx-connected':'Registered','ws-connected':'Connecting','reg-failed':'Failed',disconnected:'Offline','no-sip-password':'No SIP',connecting:'Connecting' };
const AgentLabel = { ready:'Available', busy:'On Call', break:'On Break', wrapup:'Wrap-up', offline:'Offline' };

const IcoMic = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {active && <line x1="1" y1="1" x2="23" y2="23"/>}
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);

const IcoPause = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="4" width="4" height="16" rx="1"/>
    <rect x="14" y="4" width="4" height="16" rx="1"/>
  </svg>
);

const IcoRecord = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none"/>
  </svg>
);

const IcoTransfer = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9"/>
    <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <polyline points="7 23 3 19 7 15"/>
    <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);

const IcoPhone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 3.08 4.18 2 2 0 0 1 5.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const IcoPerson = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const IcoChevron = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const isoToFlag = (iso) => (iso || 'AE').toUpperCase().replace(/[A-Z]/g, c => String.fromCodePoint(127397 + c.charCodeAt(0)));

export default function Dialer({ user, sipPassword, sipUsername, onLogout, embedded = false, onStatusChange = null, dialTo = '', onDialUsed = null, changeAgentStateRef = null, onOpenAIInbox = null, onOpenHistory = null }) {
  const [number, setNumber] = useState('');
  const [status, setStatus] = useState('ready');
  const [registrationState, setRegistrationState] = useState('pbx-connected');

  const [callPage, setCallPage] = useState(1);
  const callsPerPage = 10;

  const [selectedCli, setSelectedCli] = useState('');
  const [history, setHistory] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [incoming, setIncoming] = useState(null);

  const [muted, setMuted] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [timer, setTimer] = useState(0);

  const [callStats, setCallStats] = useState({
    duration: '00:00',
    state: 'idle',
    direction: '',
    remoteNumber: '',
    reconnecting: false,
    rtt: 0,
    jitter: 0,
    packetsLost: 0
  });

  const [agentState, setAgentState] = useState('ready');
  const [queueName, setQueueName] = useState('default');

  const [liveConnected, setLiveConnected] = useState(false);
  const [wallboardSnapshot, setWallboardSnapshot] = useState(null);

  const [transferNumber, setTransferNumber] = useState('');
  const [showTransfer, setShowTransfer] = useState(false);

  const [crmCustomer, setCrmCustomer] = useState(null);
  const [crmCallUuid, setCrmCallUuid] = useState(null);
  const [crmPhone, setCrmPhone] = useState(null);

  const [newContactName, setNewContactName] = useState('');
  const [newContactNumber, setNewContactNumber] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactCompany, setNewContactCompany] = useState('');
  const [newContactNotes, setNewContactNotes] = useState('');
  const [editingContactId, setEditingContactId] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactSort, setContactSort] = useState('company');
  const [groupByCompany, setGroupByCompany] = useState(true);

  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [activeSideTab, setActiveSideTab] = useState('calls');

  // Favourites — persisted in localStorage per user
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(`ce_fav_${user.username}`);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showFavAdd, setShowFavAdd] = useState(false);
  const [favName, setFavName]       = useState('');
  const [favNumber, setFavNumber]   = useState('');


  // AI Assistant state
  const [aiPanel, setAiPanel] = useState(null); // null | 'summary' | 'actions' | 'sentiment'
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showQueueModal, setShowQueueModal] = useState(false);
  const [showConfModal, setShowConfModal] = useState(false);
  const [confRooms, setConfRooms] = useState([]);
  const [myQueues, setMyQueues] = useState([]);
  const [queueSessions, setQueueSessions] = useState({});
  const [queueActionLoading, setQueueActionLoading] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [microphones, setMicrophones] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [selectedRingDevice, setSelectedRingDevice] = useState('');
  const [micPermission, setMicPermission] = useState('unknown');

  const timerRef = useRef(null);
  const callStartRef = useRef(null);
  const currentCallRef = useRef(null);
  const activeNumberRef = useRef('');
  const activeCliRef = useRef('');
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const activeCallIdRef = useRef(null);
  const finishCurrentCallRef = useRef(null);


  // Load queue memberships for the modal
  const loadQueues = async () => {
    try {
      const data = await api.getMyQueueMemberships();
      setMyQueues(data || []);
    } catch {}
  };

  const loadConfRooms = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const r = await fetch('/api/conferences/rooms', {
        headers: { Authorization: 'Bearer ' + token }
      });
      const d = await r.json();
      setConfRooms(Array.isArray(d) ? d.filter(x => x.is_active) : []);
    } catch {}
  };

  // Queue login/logout
  const handleQueueToggle = async (q) => {
    const key = q.queue_name;
    const isLoggedIn = queueSessions[key];
    setQueueActionLoading(key);
    try {
      const token = localStorage.getItem('token') || '';
      const endpoint = isLoggedIn ? '/api/agent/queue-logout' : '/api/agent/queue-login';
      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ queue_name: key }),
      });
      setQueueSessions(prev => ({ ...prev, [key]: !isLoggedIn }));
    } catch {}
    setQueueActionLoading('');
  };

  // AI feature handler
  const handleAiFeature = async (feature) => {
    setAiPanel(feature);
    setAiLoading(true);
    setAiResult(null);
    // Simulate AI processing delay
    await new Promise(r => setTimeout(r, 900 + Math.random()*600));
    const callerNum = callStats.remoteNumber || activeNumberRef.current || incoming?.from || 'Unknown';
    const durSecs = timer || 0;
    const dur = durSecs > 0 ? fmt(durSecs) : (callStats.duration || '00:00');
    const now = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

    if (feature === 'summary') {
      const callType = callStats.direction === 'inbound' ? 'inbound' : callStats.state !== 'idle' ? 'outbound' : 'recent';
      setAiResult({
        type: 'summary',
        title: 'Call Summary',
        content: callType === 'inbound'
          ? `Inbound call received from ${callerNum} at ${now}. Call duration: ${dur}. Agent ${user.display_name || user.username} handled the interaction. Customer enquiry noted for follow-up.`
          : `Outbound call placed to ${callerNum} at ${now}. Duration: ${dur}. Conversation completed successfully.`,
        tags: [callType === 'inbound' ? 'Inbound' : 'Outbound', 'AI Generated', dur],
      });
    } else if (feature === 'actions') {
      const suggestions = callStats.state !== 'idle' ? [
        { ico: '📝', text: 'Log this call in CRM with outcome notes',  action: 'open_ai' },
        { ico: '📋', text: 'Copy caller number to clipboard',           action: 'copy_number' },
        { ico: '🔁', text: 'Transfer to specialist queue',              action: 'transfer' },
        { ico: '👤', text: 'Save caller as new contact',                action: 'add_contact' },
      ] : [
        { ico: '🔔', text: 'Check AI Inbox for pending escalations',   action: 'open_ai' },
        { ico: '☎',  text: 'Log into your call queue',                  action: 'queue' },
        { ico: '👤', text: 'Add a frequently dialled contact',          action: 'add_contact' },
        { ico: '📊', text: 'Review your queue performance metrics',     action: 'noop' },
      ];
      setAiResult({ type: 'actions', title: 'Suggested Actions', items: suggestions });
    } else if (feature === 'sentiment') {
      // Real heuristic sentiment analysis based on call context
      const durSecs2 = timer || 0;
      const isInbound = callStats.direction === 'inbound' || !!incoming;
      const isQueueCall = !!(incoming?.queueName || callStats.remoteNumber?.startsWith('Q:'));
      const isLong = durSecs2 > 180;   // > 3 min may indicate problem
      const isVeryLong = durSecs2 > 420; // > 7 min likely frustrated

      // Fetch any recent transcript snippets from call if available
      let transcript = '';
      try {
        const tk = localStorage.getItem('token') || '';
        const tr = await fetch(`/api/transcripts/recent?limit=20`, { headers: { Authorization: `Bearer ${tk}` } });
        if (tr.ok) { const td = await tr.json(); transcript = (td.segments || []).map(s => s.text).join(' ').toLowerCase(); }
      } catch {}

      // Keyword-based weighted scoring
      const negKeywords = [
        ['absolutely unacceptable', -25], ['very frustrated', -22], ['terrible service', -22],
        ['this is ridiculous', -20], ['i want to cancel', -20], ['this is outrageous', -20],
        ['wasted my time', -18], ['keep putting me on hold', -18], ['never using again', -18],
        ['frustrated', -15], ['angry', -15], ['unacceptable', -15], ['terrible', -14],
        ['horrible', -14], ['awful', -13], ['disgusting', -13], ['useless', -12],
        ['worst', -12], ['waste of time', -12], ['been waiting', -10], ['long time', -8],
        ['still not resolved', -10], ['escalate', -8], ['manager', -7], ['complaint', -7],
        ['refund', -6], ['cancel', -6], ['disappointed', -6], ['upset', -6],
        ['not working', -5], ['problem', -4], ['issue', -4], ['wrong', -4],
      ];
      const posKeywords = [
        ['absolutely wonderful', 25], ['you are amazing', 22], ['best service', 20],
        ['exceeded my expectations', 20], ['extremely satisfied', 18],
        ['very happy', 15], ['very pleased', 15], ['excellent', 13],
        ['fantastic', 13], ['outstanding', 13], ['love it', 12],
        ['thank you so much', 12], ['great service', 12], ['very helpful', 11],
        ['appreciate', 9], ['helpful', 8], ['great', 7], ['perfect', 7],
        ['wonderful', 7], ['brilliant', 7], ['resolved', 8], ['satisfied', 8],
        ['thank you', 6], ['thanks', 5], ['good', 4], ['nice', 3], ['okay', 2],
      ];

      let score = 65; // Neutral baseline
      const detectedNeg = [];
      const detectedPos = [];

      // Score from transcript
      if (transcript) {
        for (const [kw, weight] of negKeywords) {
          if (transcript.includes(kw)) { score += weight; detectedNeg.push(kw.replace(/\w/g, c => c.toUpperCase())); }
        }
        for (const [kw, weight] of posKeywords) {
          if (transcript.includes(kw)) { score += weight; detectedPos.push(kw.replace(/\w/g, c => c.toUpperCase())); }
        }
      }

      // Context-based adjustments (no transcript available)
      if (!transcript) {
        if (isVeryLong && isQueueCall) score -= 18;
        else if (isLong && isQueueCall) score -= 10;
        else if (isLong) score -= 5;
        if (isQueueCall) score -= 5; // Queue calls often have frustrated customers
        if (durSecs2 > 0 && durSecs2 < 30) score += 5; // Quick resolution = positive
      }

      // Clamp to 10–98
      score = Math.max(10, Math.min(98, Math.round(score)));

      const sentiment = score >= 75 ? 'Positive' : score >= 48 ? 'Neutral' : 'Negative';
      const color = score >= 75 ? '#10B981' : score >= 48 ? '#F59E0B' : '#EF4444';

      const allDetected = sentiment === 'Positive' ? detectedPos.slice(0,4)
        : sentiment === 'Negative' ? detectedNeg.slice(0,4)
        : [...detectedNeg.slice(0,2), ...detectedPos.slice(0,2)];

      const fallbackKeywords = sentiment === 'Positive' ? ['Helpful', 'Satisfied', 'Resolved']
        : sentiment === 'Negative' ? ['Frustrated', 'Waiting', 'Unresolved']
        : ['Neutral', 'Standard', 'Processing'];

      setAiResult({
        type: 'sentiment',
        title: 'Sentiment Analysis',
        score, sentiment, color,
        notes: score >= 75
          ? 'Customer tone is positive. They appear satisfied with the interaction. Good opportunity to upsell or request a review.'
          : score >= 48
          ? `Customer tone is neutral. ${isLong ? 'Longer call duration may indicate some friction. ' : ''}Maintain empathy and confirm resolution before closing.`
          : `Customer appears frustrated${isVeryLong ? ' — very long call duration' : isLong ? ' — extended call duration' : ''}. Use empathy language, offer concrete solutions, and consider escalation if unresolved.`,
        keywords: allDetected.length > 0 ? allDetected : fallbackKeywords,
      });
    }
    setAiLoading(false);
  };

  useEffect(() => {
    loadData();
    loadMediaDevices();

    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      '';

    liveEvents.connect(token);

    liveEvents.on('connected', () => {
      setLiveConnected(true);
      console.log('PBX live events connected');
    });

    liveEvents.on('disconnected', () => {
      setLiveConnected(false);
      console.log('PBX live events disconnected');
    });

    liveEvents.on('snapshot', (data) => {
      setWallboardSnapshot(data.payload || null);
    });

    const handleAgentState = (data) => {
      const payload = data?.payload || {};

      const eventUser =
        payload.username ||
        payload.extension ||
        payload.user ||
        '';

      if (eventUser && eventUser !== user.username) {
        return;
      }

      if (payload.state) {
        setAgentState(payload.state);
      }

      if (payload.queue_name) {
        setQueueName(payload.queue_name);
      }

      if (payload.queue) {
        setQueueName(payload.queue);
      }
    };

    liveEvents.on('agent_state', handleAgentState);
    liveEvents.on('agent.state', handleAgentState);

    liveEvents.on('call_state', (data) => {
      console.log('Live call event', data);
    });

    liveEvents.on('crm_popup', (data) => {
      console.log('CRM popup event', data);
    });

    liveEvents.on('ai_transcription', (data) => {
      console.log('AI transcription event', data);
    });

    setStatus('ready');
    updateAgentPresence('ready', queueName);

    if (sipPassword && sipUsername) {
      sipService.register(sipUsername, sipPassword, user.display_name || user.username);

      sipService.on('registered', () => {
        setRegistrationState('registered');
        window.cePhone && window.cePhone.setSipStatus('online');
      });

      sipService.on('registrationFailed', () => {
        setRegistrationState('reg-failed');
        window.cePhone && window.cePhone.setSipStatus('offline');
      });

      sipService.on('connected', () => {
        setRegistrationState('ws-connected');
        window.cePhone && window.cePhone.setSipStatus('connecting');
      });

      sipService.on('disconnected', () => {
        setRegistrationState('disconnected');
        window.cePhone && window.cePhone.setSipStatus('offline');
      });

      sipService.on('unregistered', () => {
        setRegistrationState('disconnected');
        window.cePhone && window.cePhone.setSipStatus('offline');
      });

      requestNotificationPermission();

      sipService.on('incomingCall', (data) => {
        activeCliRef.current = data.from;
        const callId = `${user.username}-${Date.now()}`;
        activeCallIdRef.current = callId;
        setCrmCallUuid(callId);
        setCrmPhone(data.from);
        setCallStats(prev => ({
          ...prev,
          direction: 'inbound',
          remoteNumber: data.from
        }));
        setIncoming(data);
        if (onStatusChange) onStatusChange({ incomingFrom: data.from });

        // Show browser notification if tab is in background (e.g. Teams tab not active)
        const _qName = data.queueName || null;
        showIncomingCallNotification(data.from, _qName);
        window.cePhone && window.cePhone.notifyIncomingCall({ caller: data.from, callerId: data.displayName || data.from });

        // ── Speech announcement ───────────────────────────────────
        try {
          if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
            const _qName = data.queueName || (_queueNameRef.current && _queueNameRef.current !== 'default' ? _queueNameRef.current : null);
            const _speech = _qName
              ? `Incoming call from ${_qName}`
              : 'Personal call';
            const _speak = () => {
              const _utt = new SpeechSynthesisUtterance(_speech);
              // Pick a clear, high-quality English voice — the system default is often a
              // low-quality "compact" (or non-English) voice that garbles the words.
              const _voices = (window.speechSynthesis.getVoices && window.speechSynthesis.getVoices()) || [];
              const _en = _voices.filter(v => /^en[-_]?/i.test(v.lang || ''));
              const _pref = ['Samantha', 'Ava', 'Allison', 'Google US English', 'Microsoft Aria', 'Karen', 'Moira', 'Daniel'];
              let _voice = null;
              for (let i = 0; i < _pref.length && !_voice; i++) { _voice = _en.find(v => (v.name || '').indexOf(_pref[i]) !== -1); }
              if (!_voice) _voice = _en.find(v => /premium|enhanced/i.test(v.name || '')) || _en[0] || null;
              if (_voice) { _utt.voice = _voice; _utt.lang = _voice.lang || 'en-US'; } else { _utt.lang = 'en-US'; }
              _utt.rate = 1.0;
              _utt.pitch = 1.0;
              _utt.volume = 1.0;
              window.speechSynthesis.speak(_utt);
            };
            const _vv = (window.speechSynthesis.getVoices && window.speechSynthesis.getVoices()) || [];
            if (_vv.length) { _speak(); }
            else { window.speechSynthesis.onvoiceschanged = () => { window.speechSynthesis.onvoiceschanged = null; _speak(); }; }
          }
        } catch (_e) {}
        // CRM screen pop
        api.crmScreenpop(data.from)
          .then(result => setCrmCustomer(result?.customer || null))
          .catch(() => setCrmCustomer(null));
      });

      sipService.on('callState', (data) => {
        const { state } = data;
        if (state === 'active' || state === 'idle') clearIncomingCallNotification();
        setStatus(state === 'idle' ? 'ready' : state);
        setCallStats(prev => ({ ...prev, state }));

        if (state === 'connected') {
          startTimer();
          // Update Teams presence to Busy/InACall
          api.setTeamsPresence('Busy', 'InACall').catch(() => {});
        }

        if (state === 'ended' || state === 'failed') {
          stopTimer();
          setIncoming(null);
          window.cePhone && window.cePhone.notifyCallEnded();
          if (onStatusChange) onStatusChange({ incomingFrom: null });
          setMuted(false);
          setOnHold(false);
          setShowTransfer(false);
          setTransferNumber('');
          sendAgentState('ready');
          // Notify supervisor wallboard of remote hangup (manual hangup already calls this)
          sendLiveCallState('ended', callStats.direction, activeCliRef.current, activeNumberRef.current);
          activeCallIdRef.current = null;
          setCrmCustomer(null);
          setCrmCallUuid(null);
          setCrmPhone(null);
          // Restore Teams presence to Available when call ends
          api.setTeamsPresence('Available', 'Available').catch(() => {});
          // Reset call badge state so it doesn't stay stuck showing 'ended'
          setCallStats(prev => ({ ...prev, state: 'idle' }));
          setStatus('ready');
          if (finishCurrentCallRef.current) {
            finishCurrentCallRef.current(state).catch(() => {});
          }
        }
      });

      sipService.on('callStats', (data) => {
        setCallStats(prev => ({
          ...prev,
          rtt: data.rtt,
          jitter: data.jitter,
          packetsLost: data.packetsLost
        }));
      });

      sipService.on('error', (data) => {
        console.error('SIP error:', data.message);
        setStatus('ready');
        sendAgentState('ready');
      });

      sipService.on('transferFailed', (data) => {
        alert(`Transfer failed: ${data.reason || 'Unknown error'}`);
        setStatus('connected');
      });
    } else {
      setRegistrationState('no-sip-password');
    }

    return () => {
      try {
        liveEvents.disconnect();
      } catch (e) {
        console.warn('Live events disconnect failed', e);
      }

      sipService.unregister();
      stopTimer();
      stopRecording();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendAgentState = (state) => {
    setAgentState(state);

    if (liveEvents.ws && liveEvents.ws.readyState === WebSocket.OPEN) {
      liveEvents.ws.send(JSON.stringify({
        type: 'agent_state',
        user_id: user.id,
        username: user.username,
        state,
        queue: 'default'
      }));
    }

    // Persist to DB so supervisor polling sees the real-time state
    api.updateAgentState({ state, queue_name: null }).catch(() => {});
  };

  const sendLiveCallState = (state, direction, caller, callee, duration = 0) => {
    if (!activeCallIdRef.current) {
      activeCallIdRef.current = `${user.username}-${Date.now()}`;
    }

    if (liveEvents.ws && liveEvents.ws.readyState === WebSocket.OPEN) {
      liveEvents.ws.send(JSON.stringify({
        type: 'call_state',
        call_id: activeCallIdRef.current,
        username: user.username,
        direction,
        caller,
        callee,
        state,
        duration
      }));
    }
  };

  const updateAgentPresence = async (state, queue = queueName) => {
    try {
      setAgentState(state);

      await api.updateAgentState({
        state,
        queue_name: queue
      });

      liveEvents.send({
        type: 'agent_state',
        user_id: user.id,
        username: user.username,
        state,
        queue
      });
    } catch (e) {
      console.error('Failed to update agent presence', e);
    }
  };

  const loadData = async () => {
    const [, h, c, r] = await Promise.all([
      api.getAllowedCLI().catch(() => []),
      api.getCallHistory().catch(() => []),
      api.getContacts().catch(() => []),
      api.getRecordings().catch(() => [])
    ]);

    setHistory(h);
    setContacts(c);
    setRecordings(r);
    setSelectedCli('');
  };

  const loadMediaDevices = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      const audioOutputs = devices.filter(d => d.kind === 'audiooutput');

      setMicrophones(audioInputs);
      setSpeakers(audioOutputs);

      if (!selectedMic && audioInputs.length > 0) setSelectedMic(audioInputs[0].deviceId);
      if (!selectedSpeaker && audioOutputs.length > 0) setSelectedSpeaker(audioOutputs[0].deviceId);
      if (!selectedRingDevice && audioOutputs.length > 0) setSelectedRingDevice(audioOutputs[0].deviceId);
    } catch (e) {
      console.error('Failed to load media devices', e);
    }
  };

  const requestMicPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      stream.getTracks().forEach(track => track.stop());
      setMicPermission('granted');
      await loadMediaDevices();
      alert('Microphone permission granted.');
    } catch (e) {
      console.error('Microphone permission denied', e);
      setMicPermission('denied');
      alert('Microphone permission denied.');
    }
  };

  const applySpeakerDevice = async (deviceId) => {
    const remoteAudio = document.getElementById('remoteAudio');
    if (!remoteAudio) return;

    if (typeof remoteAudio.setSinkId !== 'function') {
      alert('Speaker selection is not supported by this browser. Use Chrome or Edge.');
      return;
    }

    try {
      await remoteAudio.setSinkId(deviceId);
      setSelectedSpeaker(deviceId);
    } catch (e) {
      console.error('Failed to set speaker device', e);
      alert('Failed to set speaker device.');
    }
  };

  const testSpeaker = async () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = 800;
      gain.gain.value = 0.2;

      oscillator.connect(gain);

      const destination = audioContext.createMediaStreamDestination();
      gain.connect(destination);

      const testAudio = new Audio();
      testAudio.srcObject = destination.stream;

      if (typeof testAudio.setSinkId === 'function' && selectedSpeaker) {
        await testAudio.setSinkId(selectedSpeaker);
      }

      await testAudio.play();
      oscillator.start();

      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
      }, 700);
    } catch (e) {
      console.error('Speaker test failed', e);
      alert('Speaker test failed.');
    }
  };

  const startTimer = () => {
    callStartRef.current = new Date();
    setTimer(0);

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimer(t => t + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const saveCallStarted = async ({ direction, callee, caller }) => {
    callStartRef.current = new Date();

    try {
      const saved = await api.saveCallHistory({
        call_uuid: activeCallIdRef.current,
        direction,
        callee_number: callee,
        caller_number: caller,
        duration: 0,
        status: 'started',
        call_start: callStartRef.current.toISOString(),
        call_end: null
      });

      currentCallRef.current = saved.id;
      await loadData();
    } catch (e) {
      console.error('Failed to save call start', e);
    }
  };

  const updateCurrentCall = async (statusText) => {
    if (!currentCallRef.current) return;

    try {
      await api.saveCallHistory({
        call_uuid: activeCallIdRef.current,
        direction: incoming ? 'inbound' : 'outbound',
        callee_number: activeNumberRef.current,
        caller_number: activeCliRef.current,
        duration: timer,
        status: statusText,
        call_start: callStartRef.current?.toISOString() || new Date().toISOString(),
        call_end: null
      });

      await loadData();
    } catch (e) {
      console.error('Failed to update call', e);
    }
  };

  const finishCurrentCall = async (finalStatus) => {
    if (isRecording) stopRecording();

    const duration = callStartRef.current
      ? Math.floor((new Date() - callStartRef.current) / 1000)
      : timer;

    // Map raw SIP terminal states to meaningful call statuses for analytics
    // SIP sends 'ended' or 'failed'; DB/reports expect 'completed'/'no-answer'
    const mappedStatus = (finalStatus === 'ended' || finalStatus === 'failed')
      ? (duration > 0 ? 'completed' : 'no-answer')
      : finalStatus;

    try {
      await api.saveCallHistory({
        call_uuid: activeCallIdRef.current,
        direction: incoming ? 'inbound' : 'outbound',
        callee_number: activeNumberRef.current || number,
        caller_number: activeCliRef.current || selectedCli || user.username,
        duration,
        status: mappedStatus,
        call_start: callStartRef.current?.toISOString() || new Date().toISOString(),
        call_end: new Date().toISOString()
      });

      await loadData();
    } catch (e) {
      console.error('Failed to save finished call', e);
    }

    currentCallRef.current = null;
    callStartRef.current = null;
  };

  finishCurrentCallRef.current = finishCurrentCall;

  // Sync status to parent (AgentPortal) when running embedded
  useEffect(() => {
    if (!onStatusChange) return;
    onStatusChange({
      registration: registrationState,
      liveConnected,
      agentState,
      callState: callStats.state,
      timer,
      muted,
      onHold,
      isRecording,
      remoteNumber: callStats.remoteNumber,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationState, liveConnected, agentState, callStats.state, timer, muted, onHold, isRecording, callStats.remoteNumber]);


  // Refresh history for live analytics
  useEffect(() => {
    const iv = setInterval(() => { loadData(); }, 30_000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  // Pre-fill dial number from history / contacts panel
  useEffect(() => {
    if (!dialTo) return;
    setNumber(dialTo);
    if (onDialUsed) onDialUsed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialTo]);

  // Expose updateAgentPresence to parent via ref (used by MyStatePage)
  const _updateAgentPresenceRef = useRef(null);
  _updateAgentPresenceRef.current = updateAgentPresence;
  const _queueNameRef = useRef(queueName);
  _queueNameRef.current = queueName;
  useEffect(() => {
    if (!changeAgentStateRef) return;
    changeAgentStateRef.current = (state) =>
      _updateAgentPresenceRef.current(state, _queueNameRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeAgentStateRef]);

  const makeCall = async (overrideNumber = null) => {
  const clean = (overrideNumber != null ? String(overrideNumber) : number).trim();
  const fromCli = selectedCli.trim();

  if (!/^\+?[0-9]{3,15}$/.test(clean)) {
    alert('Enter a valid destination number.');
    return;
  }

  if (fromCli && !/^\+?[0-9]{7,15}$/.test(fromCli)) {
    alert('Enter a valid From number.');
    return;
  }

  // iOS Safari: prime the audio element and acquire the mic here, before any
  // await that would break the user-gesture activation chain. Keep the stream
  // alive (do NOT stop tracks) so it can be handed directly to JsSIP — this
  // prevents a second getUserMedia call that iOS would block post-await.
  const remoteAudio = document.getElementById('remoteAudio');
  if (remoteAudio) { remoteAudio.muted = true; remoteAudio.play().catch(() => {}); }
  let preAcquiredStream = null;
  try {
    preAcquiredStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  } catch (e) {
    alert('Microphone access denied. Please allow microphone access in your browser settings and try again.');
    return;
  }

  const caller = fromCli || user.username;

  activeNumberRef.current = clean;
  activeCliRef.current = caller;
  const callId = `${user.username}-${Date.now()}`;
  activeCallIdRef.current = callId;
  setCrmCallUuid(callId);
  setCrmPhone(clean);

  // CRM lookup for outbound
  api.crmScreenpop(clean)
    .then(result => setCrmCustomer(result?.customer || null))
    .catch(() => setCrmCustomer(null));

  try {
    await saveCallStarted({ direction: 'outbound', callee: clean, caller });
  } catch (e) {
    console.warn('CDR save failed, continuing call', e);
  }

  setStatus('calling');
  sendAgentState('busy');

  setCallStats(prev => ({
    ...prev,
    state: 'calling',
    direction: 'outbound',
    remoteNumber: clean,
    reconnecting: false
  }));

  const session = sipService.makeCall(clean, fromCli, preAcquiredStream);

  if (!session) {
    setStatus('ready');
    sendAgentState('ready');
    setCallStats(prev => ({ ...prev, state: 'idle' }));
    alert('Not registered to the SIP server. Please log out and log in again with your SIP password.');
    return;
  }

  sendLiveCallState('ringing', 'outbound', caller, clean);
};

const answer = async () => {
  // iOS Safari: prime audio and acquire mic before the first await (same pattern
  // as makeCall). Keep stream alive and pass to JsSIP so it never re-acquires.
  const remoteAudio = document.getElementById('remoteAudio');
  if (remoteAudio) { remoteAudio.muted = true; remoteAudio.play().catch(() => {}); }
  let preAcquiredStream = null;
  try {
    preAcquiredStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
  } catch (e) {
    console.warn('Mic permission check failed:', e);
  }

  try {
    await saveCallStarted({
      direction: 'inbound',
      callee: user.username,
      caller: activeCliRef.current || 'unknown'
    });
  } catch (e) {
    console.warn('CDR save failed, continuing answer', e);
  }

  sipService.answerCall(preAcquiredStream);
  window.cePhone && window.cePhone.notifyCallAnswered();
  setIncoming(null);
  if (onStatusChange) onStatusChange({ incomingFrom: null });
  sendAgentState('busy');
  sendLiveCallState('connected', 'inbound', activeCliRef.current, user.username);
};

const reject = async () => {
  sipService.rejectCall();
  window.cePhone && window.cePhone.notifyCallEnded();
  setIncoming(null);
  sendLiveCallState('ended', 'inbound', activeCliRef.current, user.username);
};

const hangup = async () => {
  sipService.hangup();
  sendLiveCallState('ended', callStats.direction, activeCliRef.current, activeNumberRef.current);
};

const toggleMute = () => {
  const next = !muted;
  sipService.mute(next);
  setMuted(next);
};

const toggleHold = () => {
  if (onHold) {
    sipService.resume();
    setOnHold(false);
  } else {
    sipService.hold();
    setOnHold(true);
  }
};

const blindTransfer = () => {
  const cleanTransfer = transferNumber.trim();

  if (!cleanTransfer) {
    alert('Enter transfer destination.');
    return;
  }

  if (!/^\+?[0-9]{2,15}$/.test(cleanTransfer)) {
    alert('Enter a valid transfer destination (digits only, e.g. 2008 or +971501234567).');
    return;
  }

  if (callStats.state !== 'connected') {
    alert('You must be in an active connected call to transfer.');
    return;
  }

  const ok = sipService.transfer(cleanTransfer);
  if (ok) {
    setShowTransfer(false);
    setTransferNumber('');
    setStatus(`Transferring to ${cleanTransfer}…`);
  }
};

const sendDigit = (d) => {
  setNumber(n => n + d);
};

const resetContactForm = () => {
  setEditingContactId(null);
  setNewContactName('');
  setNewContactNumber('');
  setNewContactEmail('');
  setNewContactCompany('');
  setNewContactNotes('');
  setShowAddContact(false);
};

const editContact = (contact) => {
  setEditingContactId(contact.id);
  setNewContactName(contact.name || '');
  setNewContactNumber(contact.number || '');
  setNewContactEmail(contact.email || '');
  setNewContactCompany(contact.company || '');
  setNewContactNotes(contact.notes || '');
  setShowAddContact(true);
  setActiveSideTab('contacts');
};
  const saveContact = async () => {
    const name = newContactName.trim();
    const contactNumber = newContactNumber.trim();
    const email = newContactEmail.trim();
    const company = newContactCompany.trim();
    const notes = newContactNotes.trim();

    if (!name) {
      alert('Enter contact name.');
      return;
    }

    if (!/^\+?[0-9]{7,15}$/.test(contactNumber)) {
      alert('Enter a valid contact number.');
      return;
    }

    try {
      if (editingContactId) {
        await api.updateContact(editingContactId, {
          name,
          number: contactNumber,
          email,
          company,
          notes
        });
      } else {
        await api.addContact({ name, number: contactNumber, email, company, notes });
      }

      resetContactForm();
      await loadData();
      setActiveSideTab('contacts');
    } catch (e) {
      console.error('Failed to save contact', e);
      alert('Failed to save contact.');
    }
  };

  const deleteContact = async (id) => {
    try {
      await api.deleteContact(id);
      await loadData();
      setActiveSideTab('contacts');
    } catch (e) {
      console.error('Failed to delete contact', e);
      alert('Failed to delete contact.');
    }
  };

  const dialContact = async (contact) => {
    setNumber(contact.number || '');
    setActiveSideTab('calls');

    try {
      await api.updateContactLastDialed(contact.id);
      await loadData();
    } catch (e) {
      console.error('Failed to update last dialed', e);
    }
  };

  const startRecording = () => {
    const remoteAudio = document.getElementById('remoteAudio');

    if (!remoteAudio || !remoteAudio.srcObject) {
      alert('No active audio stream to record.');
      return;
    }

    recordedChunksRef.current = [];

    try {
      const recorder = new MediaRecorder(remoteAudio.srcObject, {
        mimeType: 'audio/webm'
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) recordedChunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' });
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `${user.username}-${timestamp}.webm`;

        try {
          await api.uploadRecording(blob, filename);
          await loadData();
          setActiveSideTab('recordings');
        } catch (e) {
          console.error('Failed to upload recording', e);
          alert('Recording created but failed to upload to server.');
        }
      };

      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (e) {
      console.error('Recording failed', e);
      alert('Recording failed. Browser may not support this recording format.');
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
    }

    mediaRecorderRef.current = null;
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  const deleteRecording = async (id) => {
    try {
      await api.deleteRecording(id);
      await loadData();
    } catch (e) {
      console.error('Failed to delete recording', e);
      alert('Failed to delete recording.');
    }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const formatDate = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleString();
    } catch (e) {
      return value;
    }
  };

  const formatMs = (seconds) => {
    if (!seconds) return '0 ms';
    return `${Math.round(seconds * 1000)} ms`;
  };

  const changeAgentState = (state) => {
    updateAgentPresence(state, queueName);
  };

  const addFavorite = () => {
    const num = favNumber.trim();
    if (!num) return;
    const entry = { name: favName.trim() || num, number: num };
    const updated = [...favorites, entry];
    setFavorites(updated);
    try { localStorage.setItem(`ce_fav_${user.username}`, JSON.stringify(updated)); } catch {}
    setFavName(''); setFavNumber(''); setShowFavAdd(false);
  };

  const removeFavorite = (idx) => {
    const updated = favorites.filter((_, i) => i !== idx);
    setFavorites(updated);
    try { localStorage.setItem(`ce_fav_${user.username}`, JSON.stringify(updated)); } catch {}
  };

  // Last 10 calls from history (all directions, not deduped)
  const recentDials = history.slice(0, 10).map(h => ({
    number: h.direction === 'outbound' ? h.callee_number : h.caller_number,
    direction: h.direction || 'outbound',
    when: h.call_start
      ? new Date(h.call_start).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '',
  })).filter(r => r.number);

  const filteredContacts = contacts
    .filter((contact) => {
      const q = contactSearch.trim().toLowerCase();
      if (!q) return true;

      return [
        contact.name,
        contact.number,
        contact.email,
        contact.company,
        contact.notes
      ].some(v => String(v || '').toLowerCase().includes(q));
    })
    .sort((a, b) => {
      if (contactSort === 'name') {
        return String(a.name || '').localeCompare(String(b.name || ''));
      }

      if (contactSort === 'last_dialed') {
        return new Date(b.last_dialed || 0) - new Date(a.last_dialed || 0);
      }

      return String(a.company || '').localeCompare(String(b.company || '')) ||
        String(a.name || '').localeCompare(String(b.name || ''));
    });

  const totalCallPages = Math.max(1, Math.ceil(history.length / callsPerPage));

  const pagedHistory = history.slice(
    (callPage - 1) * callsPerPage,
    callPage * callsPerPage
  );

  const groupedContacts = filteredContacts.reduce((groups, contact) => {
    const company = contact.company || 'No Company';
    if (!groups[company]) groups[company] = [];
    groups[company].push(contact);
    return groups;
  }, {});

  const renderContactCard = (contact) => (
    <div className="row" key={contact.id}>
      <strong>{contact.name}</strong>
      <br />
      <span>{contact.number}</span>

      {contact.company && (
        <>
          <br />
          <span>Company: {contact.company}</span>
        </>
      )}

      {contact.email && (
        <>
          <br />
          <span>Email: {contact.email}</span>
        </>
      )}

      {contact.last_dialed && (
        <>
          <br />
          <span>Last Dialed: {formatDate(contact.last_dialed)}</span>
        </>
      )}

      {contact.notes && (
        <>
          <br />
          <span>Notes: {contact.notes}</span>
        </>
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => dialContact(contact)}>Dial</button>
        <button onClick={() => editContact(contact)}>Edit</button>
        <button onClick={() => deleteContact(contact.id)}>Delete</button>
      </div>
    </div>
  );


  /* ── helper: get initials from name ── */
  const initials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0,2).toUpperCase();
  };

  /* ── call direction icon ── */
  const DirIcon = ({ dir, missed }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={missed ? '#EF4444' : dir === 'inbound' ? '#10B981' : '#6366F1'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {dir === 'inbound' ? <polyline points="17 8 21 12 17 16"/> : <polyline points="7 8 3 12 7 16"/>}
      {dir === 'inbound' ? <path d="M3 12h18"/> : <path d="M21 12H3"/>}
    </svg>
  );

  /* ── active call tab filter ── */
  const [recentTab, setRecentTab] = useState('all');
  const filteredHistory = history.filter(h => {
    if (recentTab === 'all')      return true;
    if (recentTab === 'inbound')  return h.direction === 'inbound';
    if (recentTab === 'outbound') return h.direction === 'outbound';
    if (recentTab === 'missed')   return h.status === 'missed' || h.status === 'no-answer';
    return true;
  }).slice(0, 5);

  /* ── quick stats from history ── */
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayCalls  = history.filter(h => (h.call_start||'').startsWith(todayStr) && h.status !== 'started');
  const totalToday  = todayCalls.length;
  const answeredToday = todayCalls.filter(h => h.status === 'answered' || h.status === 'completed').length;
  const missedToday   = todayCalls.filter(h => h.status === 'missed'   || h.status === 'no-answer').length;
  const avgTalkSecs   = answeredToday > 0
    ? Math.round(todayCalls.filter(h => h.duration > 0).reduce((s,h) => s+h.duration, 0) / Math.max(1, answeredToday))
    : 0;
  const svcLevel = totalToday > 0 ? Math.round((answeredToday / totalToday) * 100) : 100;

  const isActive = ['calling','ringing','connected'].includes(callStats.state);
  const isConnected = callStats.state === 'connected';

  /* ── active call contact name ── */
  const activeContact = contacts.find(c => c.number === callStats.remoteNumber) || null;
  const activeDisplayName = activeContact?.name || callStats.remoteNumber || '—';

  /* ── state badge color ── */
  const STATE_CLR = { ready:'#10B981', busy:'#EF4444', break:'#F59E0B', wrapup:'#6366F1', offline:'#94A3B8' };
  const SIP_CLR   = { registered:'#10B981','pbx-connected':'#10B981','ws-connected':'#F59E0B','reg-failed':'#EF4444',disconnected:'#EF4444','no-sip-password':'#EF4444',connecting:'#F59E0B' };

  const isCC = user.cc_enabled || user.queue_enabled || user.role === 'agent' || user.role === 'supervisor';
  const showAI = can(user, 'ai_transcription');

  return (
    <div className="dv2-shell">
      <audio id="remoteAudio" autoPlay playsInline />

      {/* ═══════════════════════════════ INCOMING CALL OVERLAY ══════════════════════════════ */}
      {incoming && (
        <div className="dv2-incoming-overlay">
          <div className="dv2-incoming-card">
            <div className="dv2-incoming-pulse" />
            <div className="dv2-incoming-avatar">
              {initials(incoming.fromName || incoming.from)}
            </div>
            <div className="dv2-incoming-label">Incoming Call</div>
            <div className="dv2-incoming-name">{incoming.fromName || incoming.from}</div>
            {incoming.queueName && (
              <div className="dv2-incoming-queue">via {incoming.queueName}</div>
            )}
            <div className="dv2-incoming-number">{incoming.from}</div>
            <div className="dv2-incoming-actions">
              <button className="dv2-btn-reject" onClick={reject}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M4.93 4.93a10 10 0 0 1 14.14 14.14L4.93 4.93zm14.14 14.14A10 10 0 0 1 4.93 4.93L19.07 19.07zM1 1l22 22" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>
                Decline
              </button>
              <button className="dv2-btn-answer" onClick={answer}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 3.08 4.18 2 2 0 0 1 5.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                Answer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════ TOP SEARCH / STATS BAR ═════════════════════════════ */}
      <div className="dv2-topbar">
        <div className="dv2-search-wrap">
          <svg className="dv2-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            className="dv2-search-input"
            placeholder="Search contacts, numbers, queues…"
            value={contactSearch}
            onChange={e => { setContactSearch(e.target.value); }}
          />
        </div>

        <div className="dv2-topbar-stats">
          <div className="dv2-stat-chip">
            <span className="dv2-stat-chip-label">Calls Today</span>
            <span className="dv2-stat-chip-val">{totalToday}</span>
            <span className="dv2-stat-chip-sub"><span style={{color:'#10B981'}}>↑{answeredToday}</span> <span style={{color:'#EF4444'}}>↓{missedToday}</span></span>
          </div>

          {(wallboardSnapshot?.queues?.[0] || queueName !== 'default') && (
            <div className="dv2-stat-chip dv2-queue-chip">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
              <div>
                <div className="dv2-stat-chip-label">Queue Status</div>
                <div className="dv2-stat-chip-val" style={{fontSize:'13px'}}>{queueName !== 'default' ? queueName : 'Support Queue'}</div>
              </div>
            </div>
          )}
        </div>

        <div className="dv2-topbar-right">
          <button className="dv2-icon-btn" onClick={() => setShowSettings(s => !s)} title="Audio Settings">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M20 12h-2M6 12H4M19.07 19.07l-1.41-1.41M4.93 19.07l1.41-1.41M12 20v-2M12 6V4"/></svg>
          </button>
          <div className="dv2-user-badge">
            <div className="dv2-user-avatar" style={{background: 'linear-gradient(135deg,#b8963a,#C9A84C)'}}>
              {initials(user.display_name || user.username)}
            </div>
            <div>
              <div className="dv2-user-name">{user.display_name || user.username}</div>
              <div className="dv2-user-ext">
                <span style={{background: STATE_CLR[agentState] || '#94A3B8', width:7, height:7, borderRadius:'50%', display:'inline-block', marginRight:4}}/>
                Ext {sipUsername || user.extension_number}
              </div>
            </div>
            {!embedded && <button className="dv2-logout-btn" onClick={onLogout}>Logout</button>}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════ SETTINGS PANEL ══════════════════════════════════════ */}
      {showSettings && (
        <div className="dv2-settings-panel">
          <div className="dv2-settings-header">
            <span>🎙 Audio Settings</span>
            <button className="dv2-close-btn" onClick={() => setShowSettings(false)}>✕</button>
          </div>
          <div className="dv2-settings-grid">
            <div>
              <label className="dv2-settings-label">Microphone</label>
              <select className="dv2-settings-select" value={selectedMic} onChange={e => setSelectedMic(e.target.value)}>
                {microphones.length === 0 && <option value="">No microphone detected</option>}
                {microphones.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0,6)}`}</option>)}
              </select>
              <button className="dv2-settings-btn" onClick={requestMicPermission}>Request Access</button>
              <span className="dv2-settings-hint">Permission: {micPermission}</span>
            </div>
            <div>
              <label className="dv2-settings-label">Speaker</label>
              <select className="dv2-settings-select" value={selectedSpeaker} onChange={e => applySpeakerDevice(e.target.value)}>
                {speakers.length === 0 && <option value="">Default speaker</option>}
                {speakers.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Speaker ${d.deviceId.slice(0,6)}`}</option>)}
              </select>
              <button className="dv2-settings-btn" onClick={testSpeaker}>🔊 Test Speaker</button>
            </div>
            <div>
              <label className="dv2-settings-label">Ringing Device</label>
              <select className="dv2-settings-select" value={selectedRingDevice} onChange={e => setSelectedRingDevice(e.target.value)}>
                {speakers.length === 0 && <option value="">Default</option>}
                {speakers.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Speaker ${d.deviceId.slice(0,6)}`}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════ 3-COLUMN MAIN GRID ═════════════════════════════════ */}
      <div className="dv2-grid">

        {/* ──── COL 1: DIALER ──────────────────────────────────────────────── */}
        <div className="dv2-card dv2-dialer-card">

          {/* Identity row */}
          <div className="dv2-id-row">
            <div className="dv2-id-avatar">
              {initials(user.display_name || user.username)}
            </div>
            <div>
              <div className="dv2-id-name">{user.display_name || user.username}</div>
              <div className="dv2-id-ext">Ext {sipUsername || user.extension_number}</div>
            </div>
            <button className="dv2-audio-btn" onClick={() => setShowSettings(s => !s)} title="Audio Settings">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
            </button>
          </div>

          {user && user.allow_custom_cid && (
            <div style={{marginBottom:8}}>
              <input
                className="dv2-number-input"
                style={{width:'100%'}}
                value={selectedCli}
                onChange={e => setSelectedCli(e.target.value.replace(/[^0-9+]/g,''))}
                placeholder="From number (leave blank = your default DID)"
                title="Custom caller ID for outbound calls. Blank = your assigned DID."
              />
            </div>
          )}
          {/* Number input */}
          <div className="dv2-number-row">
            <div className="dv2-flag-select">
              <span>{isoToFlag(user && user.default_country)}</span>
              <select
                value={selectedCli}
                onChange={e => setSelectedCli(e.target.value)}
                className="dv2-cli-select"
              >
                <option value="">{(user && user.default_dial_code) || '+971'}</option>
              </select>
            </div>
            <input
              className="dv2-number-input"
              value={number}
              onChange={e => setNumber(e.target.value)}
              placeholder="50 123 4567"
              onKeyDown={e => e.key === 'Enter' && makeCall()}
            />
            {number && (
              <button className="dv2-clear-x" onClick={() => setNumber('')}>✕</button>
            )}
          </div>


          {/* Status row */}
          <div className="dv2-status-row">
            <div className="dv2-status-pill">
              <span className="dv2-dot" style={{background: SIP_CLR[registrationState] || '#94A3B8'}} />
              <div>
                <div className="dv2-status-label">SIP</div>
                <div className="dv2-status-val">{registrationState === 'registered' || registrationState === 'pbx-connected' ? 'Registered' : registrationState === 'ws-connected' ? 'Connecting' : 'Offline'}</div>
              </div>
            </div>
            <div className="dv2-status-pill">
              <span className="dv2-dot" style={{background: liveConnected ? '#10B981' : '#94A3B8'}} />
              <div>
                <div className="dv2-status-label">Network</div>
                <div className="dv2-status-val">{liveConnected ? 'Connected' : 'Offline'}</div>
              </div>
            </div>
            <div className="dv2-status-pill">
              <span className="dv2-dot" style={{background: STATE_CLR[agentState] || '#94A3B8'}} />
              <div>
                <div className="dv2-status-label">Status</div>
                <div className="dv2-status-val">{AgentLabel[agentState] || agentState}</div>
              </div>
            </div>
          </div>

          {/* Keypad */}
          <div className="dv2-keypad">
            {KEYPAD.map(({ d, sub }) => (
              <button key={d} className="dv2-key" onClick={() => sendDigit(d)}>
                <span className="dv2-key-d">{d}</span>
                {sub && <span className="dv2-key-sub">{sub}</span>}
              </button>
            ))}
          </div>

          {/* Primary call actions */}
          <div className="dv2-call-actions">
            <button className="dv2-btn-secondary dv2-redial-btn"
              onClick={() => { const n = recentDials[0]?.number || ''; if(n){ setNumber(n); setTimeout(() => makeCall(), 80); } }}
              disabled={!recentDials[0]?.number}
              title={recentDials[0]?.number ? `Redial ${recentDials[0].number}` : 'No recent call'}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.8"/></svg>
              Redial
            </button>
            <button className="dv2-btn-secondary dv2-vm-btn" onClick={() => { setNumber('*97'); setTimeout(() => makeCall('*97'), 100); }} title="Check voicemail — dials *97">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="5.5" cy="11.5" r="4.5"/><circle cx="18.5" cy="11.5" r="4.5"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
              Voicemail
            </button>
            <button
              className={`dv2-btn-call ${isActive ? 'dv2-btn-call-active' : ''}`}
              onClick={() => isActive ? hangup() : makeCall()}
            >
              {isActive ? (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round"/></svg>
                  End Call
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 3.08 4.18 2 2 0 0 1 5.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  Call Now
                </>
              )}
            </button>
            <button className="dv2-btn-secondary" onClick={() => setNumber('')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
              Clear
            </button>
          </div>

          {/* In-call controls */}
          {isActive && (
            <div className="dv2-incall-controls">
              <button className={`dv2-ctrl-btn ${muted ? 'dv2-ctrl-active' : ''}`} onClick={toggleMute}>
                <IcoMic active={muted} />
                <span>{muted ? 'Unmute' : 'Mute'}</span>
              </button>
              <button className={`dv2-ctrl-btn ${onHold ? 'dv2-ctrl-active' : ''}`} onClick={toggleHold}>
                <IcoPause />
                <span>{onHold ? 'Resume' : 'Hold'}</span>
              </button>
              {can(user, 'transfer') && (
              <button className={`dv2-ctrl-btn ${showTransfer ? 'dv2-ctrl-active' : ''}`} onClick={() => setShowTransfer(s => !s)}>
                <IcoTransfer />
                <span>Xfer</span>
              </button>
              )}
              {can(user, 'recording') && (
                <button className={`dv2-ctrl-btn ${isRecording ? 'dv2-ctrl-rec' : ''}`} onClick={toggleRecording}>
                  <IcoRecord />
                  <span>{isRecording ? 'Stop' : 'Record'}</span>
                </button>
              )}
              <button className="dv2-ctrl-btn" onClick={() => {}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
                <span>More</span>
              </button>
            </div>
          )}

          {/* Transfer panel */}
          {showTransfer && isActive && (
            <div className="dv2-transfer-panel">
              <input className="dv2-transfer-input" value={transferNumber} onChange={e => setTransferNumber(e.target.value)} placeholder="Transfer destination…" />
              <button className="dv2-transfer-go" onClick={blindTransfer}>Blind Transfer</button>
            </div>
          )}

          {/* Agent state selector — CC agents only */}
          {isCC && (
            <div className="dv2-agent-states">
              {[
                { s:'ready',   label:'Available', color:'#10B981' },
                { s:'busy',    label:'Busy',      color:'#EF4444' },
                { s:'wrapup',  label:'Wrap Up',   color:'#6366F1' },
                { s:'offline', label:'Offline',   color:'#94A3B8' },
              ].map(({ s, label, color }) => (
                <button
                  key={s}
                  className={`dv2-state-btn ${agentState === s ? 'dv2-state-active' : ''}`}
                  style={agentState === s ? { borderColor: color, color, background: color + '18' } : {}}
                  onClick={() => changeAgentState(s)}
                >
                  <span style={{background: color, width:7, height:7, borderRadius:'50%', flexShrink:0}} />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>


        {/* ──── COL 3: RECENT CALLS (last 5) ──────────────────────────────────── */}
        <div className="dv2-col-right">

          {/* Recent Calls */}
          <div className="dv2-card dv2-recent-card" style={{overflowY:'auto',maxHeight:'80vh'}}>
            <div className="dv2-section-header">
              <div className="dv2-section-title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                Recent Calls
              </div>
              <button className="dv2-view-all" onClick={() => onOpenHistory && onOpenHistory()}>View all</button>
            </div>

            <div className="dv2-recent-tabs">
              {['all','inbound','outbound','missed'].map(t => (
                <button
                  key={t}
                  className={`dv2-tab ${recentTab === t ? 'dv2-tab-active' : ''}`}
                  onClick={() => setRecentTab(t)}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>

            <div className="dv2-rc-list">
              {filteredHistory.length === 0 && (
                <div className="dv2-recent-empty" style={{textAlign:'center',padding:'32px 0',color:'#94A3B8',fontSize:13}}>
                  <div style={{fontSize:32,marginBottom:8}}>📭</div>No calls yet
                </div>
              )}
              {filteredHistory.slice(0, 50).map((h, i) => {
                const num      = h.direction === 'outbound' ? h.callee_number : h.caller_number;
                const isMissed = h.status === 'missed' || h.status === 'no-answer';
                const contact  = contacts.find(c => c.number === num);
                const hasContact = !!contact;
                const displayName = hasContact ? contact.name : null;
                const timeStr  = h.call_start
                  ? new Date(h.call_start).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })
                  : '';
                const accentClr = isMissed ? '#EF4444' : h.direction === 'inbound' ? '#10B981' : '#6366F1';
                const dirLabel  = isMissed ? 'Missed' : h.direction === 'inbound' ? 'Inbound' : 'Outbound';
                const dirSymbol = isMissed ? '↓' : h.direction === 'inbound' ? '↙' : '↗';
                const avColor   = isMissed ? '#FEE2E2' : h.direction === 'inbound' ? '#D1FAE5' : '#E0E7FF';
                const avText    = isMissed ? '#991B1B' : h.direction === 'inbound' ? '#065F46' : '#3730A3';
                return (
                  <div
                    key={h.id || i}
                    className="dv2-rc-item"
                    style={{
                      display:'flex', alignItems:'center', gap:10,
                      padding:'9px 12px', borderRadius:12, marginBottom:4,
                      background:'#fff',
                      border:'1px solid #F1F5F9',
                      borderLeft:`3px solid ${accentClr}`,
                      transition:'all .15s',
                      cursor:'default',
                      position:'relative',
                    }}
                    onMouseEnter={e=>{ e.currentTarget.style.background='#F8FAFC'; e.currentTarget.style.borderColor=accentClr; e.currentTarget.querySelector('.dv2-rc-callbtn').style.opacity='1'; e.currentTarget.querySelector('.dv2-rc-callbtn').style.transform='scale(1)'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background='#fff'; e.currentTarget.style.borderLeftColor=accentClr; e.currentTarget.style.borderTopColor='#F1F5F9'; e.currentTarget.style.borderRightColor='#F1F5F9'; e.currentTarget.style.borderBottomColor='#F1F5F9'; e.currentTarget.querySelector('.dv2-rc-callbtn').style.opacity='0'; e.currentTarget.querySelector('.dv2-rc-callbtn').style.transform='scale(0.85)'; }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width:36, height:36, borderRadius:'50%', flexShrink:0,
                      background:avColor, color:avText,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontWeight:800, fontSize:12, letterSpacing:'-0.5px',
                    }}>
                      {initials(displayName || num || '?')}
                    </div>

                    {/* Info */}
                    <div style={{flex:1, minWidth:0}}>
                      {/* Name row */}
                      {displayName
                        ? <div style={{fontSize:13,fontWeight:700,color:'#0F172A',
                            overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                            title={displayName}>{displayName}</div>
                        : null
                      }
                      {/* Number — always shown, monospace-style */}
                      <div style={{
                        fontSize:12, fontWeight:600,
                        color: displayName ? '#475569' : (isMissed ? '#EF4444' : '#0F172A'),
                        fontVariantNumeric:'tabular-nums', letterSpacing:'0.2px',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                        marginTop: displayName ? 1 : 0,
                      }} title={num}>{num || '—'}</div>
                      {/* Direction + duration */}
                      <div style={{display:'flex',alignItems:'center',gap:5,marginTop:3}}>
                        <span style={{
                          fontSize:10, fontWeight:700,
                          color:accentClr, letterSpacing:'0.3px',
                        }}>{dirSymbol} {dirLabel}</span>
                        {h.duration > 0 && (
                          <span style={{fontSize:10,color:'#94A3B8'}}>· {fmt(h.duration)}</span>
                        )}
                      </div>
                    </div>

                    {/* Time + call button stack */}
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6,flexShrink:0}}>
                      <span style={{
                        fontSize:10, color:'#94A3B8', fontWeight:600,
                        fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap',
                      }}>{timeStr}</span>
                      <button
                        className="dv2-rc-callbtn"
                        onClick={() => makeCall(num)}
                        title={`Call ${num}`}
                        style={{
                          width:28, height:28, borderRadius:'50%', border:'none',
                          background: accentClr, color:'#fff',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          cursor:'pointer', opacity:0, transform:'scale(0.85)',
                          transition:'opacity .15s, transform .15s',
                          flexShrink:0,
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 3.08 4.18 2 2 0 0 1 5.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>


        </div>{/* /dv2-col-right */}

        {/* ──── COL 4: ANALYTICS + AI COACHING ─────────────────────────────── */}
        <div className="dv2-col-panel">

          {/* ── Analytics Dashboard ── */}
          <div className="dv2-card" style={{padding:'16px 18px'}}>
            <div className="dv2-section-header" style={{marginBottom:14}}>
              <div className="dv2-section-title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                Call Analytics
              </div>
              <span className="dv2-today-badge">Today</span>
            </div>

            {/* KPI tiles — 4 columns like a wallboard */}
            {(() => {
              const inbound  = todayCalls.filter(h => (h.direction||'').toLowerCase() === 'inbound').length;
              const outbound = todayCalls.filter(h => (h.direction||'').toLowerCase() === 'outbound').length;
              const ansRate  = totalToday > 0 ? Math.round((answeredToday / totalToday) * 100) : 100;

              /* ── SVG Gauge helper ── */
              /* 270° arc gauge: starts at 225° (lower-left = 7-o'clock), sweeps CW to -45° (lower-right = 5-o'clock) */
              const mkGauge = (val, max, label, unit, zones) => {
                const R=40, cx=56, cy=56, vw=112, vh=82;
                const PI=Math.PI;
                const toXY = deg => [cx + R*Math.cos(deg*PI/180), cy - R*Math.sin(deg*PI/180)];
                const pct = Math.min(1, Math.max(0, val / max));

                /* arc endpoints */
                const [sx,sy] = toXY(225);
                const [ex,ey] = toXY(-45);

                /* fill arc (CW from start) */
                const fillDeg = 225 - pct * 270;
                const [fx,fy] = toXY(fillDeg);
                const laf = pct > 2/3 ? 1 : 0;

                /* zone arc fragments */
                const zoneArcs = zones ? [
                  { p1:0,       p2:zones[0], color:'rgba(239,68,68,.35)'  },
                  { p1:zones[0],p2:zones[1], color:'rgba(245,158,11,.35)' },
                  { p1:zones[1],p2:1,        color:'rgba(16,185,129,.35)' },
                ].map(z => {
                  const [ax,ay] = toXY(225 - z.p1*270);
                  const [bx,by] = toXY(225 - z.p2*270);
                  const zla = (z.p2-z.p1) > 2/3 ? 1 : 0;
                  return { ...z, ax, ay, bx, by, zla };
                }) : [];

                /* fill color */
                const color = zones
                  ? (pct >= zones[1] ? '#10B981' : pct >= zones[0] ? '#F59E0B' : '#EF4444')
                  : '#6366F1';

                /* display label */
                const dispVal = unit === '%'
                  ? `${val}%`
                  : unit === 'time'
                    ? `${Math.floor(val/60)}:${String(val%60).padStart(2,'0')}`
                    : val;

                return (
                  <div style={{textAlign:'center'}}>
                    <svg viewBox={`0 0 ${vw} ${vh}`} style={{width:'100%',height:'70px',overflow:'visible'}}>

                      {/* Tick marks */}
                      {Array.from({length:9},(_,i)=>{
                        const tickDeg = 225 - (i/8)*270;
                        const [ix,iy] = toXY(tickDeg);
                        const [ox,oy] = [cx+(R+5)*Math.cos(tickDeg*PI/180), cy-(R+5)*Math.sin(tickDeg*PI/180)];
                        return <line key={i} x1={ix} y1={iy} x2={ox} y2={oy} stroke="#CBD5E1" strokeWidth={i%2===0?1.5:0.8}/>;
                      })}

                      {/* Zone background arcs */}
                      {zoneArcs.map((z,i)=>(
                        <path key={i} d={`M ${z.ax} ${z.ay} A ${R} ${R} 0 ${z.zla} 1 ${z.bx} ${z.by}`}
                          fill="none" stroke={z.color} strokeWidth={14} strokeLinecap="butt"/>
                      ))}

                      {/* Track */}
                      <path d={`M ${sx} ${sy} A ${R} ${R} 0 1 1 ${ex} ${ey}`}
                        fill="none" stroke="#E2E8F0" strokeWidth={10} strokeLinecap="round"/>

                      {/* Fill arc */}
                      {pct > 0 && (
                        <path d={`M ${sx} ${sy} A ${R} ${R} 0 ${laf} 1 ${fx} ${fy}`}
                          fill="none" stroke={color} strokeWidth={10} strokeLinecap="round"/>
                      )}

                      {/* Center value — needle removed */}
                      <text x={cx} y={cy+6} fontSize="16" fontWeight="800" fill="#0F172A" textAnchor="middle" dominantBaseline="middle">{dispVal}</text>
                    </svg>
                    <div style={{fontSize:10,fontWeight:700,color:'#64748B',textTransform:'uppercase',letterSpacing:'0.8px',marginTop:10}}>{label}</div>
                  </div>
                );
              };

              return (
                <div>
                  {/* 4 KPI tiles */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:7,marginBottom:16}}>
                    {[
                      {label:'Total Calls', val:totalToday, bg:'linear-gradient(135deg,#1E40AF,#3B82F6)', icon:'📞'},
                      {label:'Outgoing',    val:outbound,   bg:'linear-gradient(135deg,#92400E,#D97706)', icon:'↗'},
                      {label:'Incoming',    val:inbound,    bg:'linear-gradient(135deg,#064E3B,#10B981)', icon:'↙'},
                      {label:'Missed',      val:missedToday,bg:'linear-gradient(135deg,#7F1D1D,#EF4444)', icon:'✗'},
                    ].map(t => (
                      <div key={t.label} style={{background:t.bg,borderRadius:11,padding:'8px 5px',textAlign:'center',position:'relative',overflow:'hidden'}}>
                        <div style={{position:'absolute',top:5,right:6,fontSize:13,opacity:.2}}>{t.icon}</div>
                        <div style={{fontSize:18,fontWeight:800,color:'#fff',lineHeight:1}}>{t.val}</div>
                        <div style={{fontSize:9,color:'rgba(255,255,255,.65)',marginTop:4,textTransform:'uppercase',letterSpacing:'0.5px',fontWeight:700}}>{t.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* 3 gauges */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,padding:'8px 4px 4px'}}>
                    {mkGauge(svcLevel, 100, 'Service Level', '%', [0.5, 0.8])}
                    {mkGauge(Math.round(avgTalkSecs), 600, 'Avg Talk Time', 'time', null)}
                    {mkGauge(ansRate, 100, 'Answer Rate', '%', [0.5, 0.8])}
                  </div>

                  {/* Mini sparkline */}
                  {(() => {
                    const HOURS=8, W=300, H=40, PAD=4;
                    const now=new Date();
                    const bkts=Array.from({length:HOURS},(_,i)=>{
                      const h=(now.getHours()-HOURS+1+i+24)%24;
                      const ans=todayCalls.filter(c=>{try{return new Date(c.call_start).getHours()===h&&(c.status==='answered'||c.status==='completed');}catch{return false;}}).length;
                      const mis=todayCalls.filter(c=>{try{return new Date(c.call_start).getHours()===h&&(c.status==='missed'||c.status==='no-answer');}catch{return false;}}).length;
                      const hStr=h<10?`0${h}:00`:`${h}:00`;
                      return{h,ans,mis,hStr};
                    });
                    const maxV=Math.max(1,...bkts.map(b=>b.ans+b.mis));
                    const xStep=W/Math.max(HOURS-1,1);
                    const yP=v=>H-PAD-((v/maxV)*(H-PAD*2));
                    const aPts=bkts.map((b,i)=>[i*xStep,yP(b.ans)]);
                    const mPts=bkts.map((b,i)=>[i*xStep,yP(b.mis)]);
                    const aArea=aPts.map((p,i)=>(i===0?'M':'L')+p[0].toFixed(1)+' '+p[1].toFixed(1)).join(' ')+` L${W} ${H} L0 ${H} Z`;
                    return (
                      <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid #F1F5F9'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                          <span style={{fontSize:10,fontWeight:700,color:'#94A3B8',textTransform:'uppercase',letterSpacing:'0.8px'}}>Hourly Activity</span>
                          <div style={{display:'flex',gap:10,fontSize:10}}>
                            <span style={{color:'#10B981'}}>● Answered</span>
                            <span style={{color:'#EF4444'}}>— Missed</span>
                          </div>
                        </div>
                        <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:40,display:'block'}} preserveAspectRatio="none">
                          <defs>
                            <linearGradient id="spG3" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#10B981" stopOpacity="0.25"/>
                              <stop offset="100%" stopColor="#10B981" stopOpacity="0"/>
                            </linearGradient>
                          </defs>
                          <path d={aArea} fill="url(#spG3)"/>
                          <polyline points={aPts.map(p=>p.join(',')).join(' ')} fill="none" stroke="#10B981" strokeWidth="1.8" strokeLinejoin="round"/>
                          <polyline points={mPts.map(p=>p.join(',')).join(' ')} fill="none" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="3 2" strokeLinejoin="round"/>
                        </svg>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:9,color:'#CBD5E1',marginTop:3}}>
                          <span>{bkts[0].hStr}</span>
                          <span>{bkts[Math.floor(HOURS/2)].hStr}</span>
                          <span>{bkts[HOURS-1].hStr}</span>
                        </div>
                      </div>
                    );
                  })()}

                </div>
              );
            })()}
          </div>

                </div>{/* /dv2-col-panel */}

        {/* ──── SUPPORT ROW: spans recent + analytics ──────────────────────────── */}
        {showAI && (
        <div className="dv2-col-support" style={{overflow:'hidden'}}>
          <div className="dv2-card" style={{height:'100%',padding:0,overflow:'hidden',display:'flex',flexDirection:'column'}}>
            <QueueAIPanel
              user={user}
              callerNumber={callStats.remoteNumber || undefined}
              callerName={activeDisplayName || undefined}
            />
          </div>
        </div>
        )}

      </div>{/* /dv2-grid */}

      {/* ═══════════════════════════════ ACTIVE CALL BOTTOM BAR ══════════════════════════════ */}
      {isConnected && (
        <div className="dv2-call-bar">
          <div className="dv2-call-bar-left">
            <div className="dv2-call-av" style={{background: avatarBg(0)}}>
              {initials(activeDisplayName)}
            </div>
            <div>
              <div className="dv2-call-bar-name">{activeDisplayName}</div>
              <div className="dv2-call-bar-num">{callStats.remoteNumber}</div>
            </div>
          </div>

          <div className="dv2-call-bar-center">
            <span className="dv2-connected-badge">Connected</span>
            <span className="dv2-call-timer">{fmt(timer)}</span>
            {/* Waveform */}
            <div className="dv2-waveform">
              {Array.from({length: 18}).map((_, i) => (
                <div key={i} className="dv2-wave-bar" style={{animationDelay: `${i * 0.07}s`}} />
              ))}
            </div>
          </div>

          <div className="dv2-call-bar-controls">
            <button className={`dv2-bar-btn ${muted ? 'dv2-bar-btn-active' : ''}`} onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}>
              <IcoMic active={muted} />
              <span>Mute</span>
            </button>
            <button className={`dv2-bar-btn ${onHold ? 'dv2-bar-btn-active' : ''}`} onClick={toggleHold} title="Hold">
              <IcoPause />
              <span>Hold</span>
            </button>
            <button className="dv2-bar-btn" title="Speaker">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
              <span>Speaker</span>
            </button>
            <button className="dv2-bar-btn" onClick={() => {}} title="Keypad">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="6" r="1.5"/><circle cx="12" cy="6" r="1.5"/><circle cx="19" cy="6" r="1.5"/><circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/><circle cx="5" cy="18" r="1.5"/><circle cx="12" cy="18" r="1.5"/><circle cx="19" cy="18" r="1.5"/></svg>
              <span>Keypad</span>
            </button>
            {can(user, 'recording') && (
              <button className={`dv2-bar-btn ${isRecording ? 'dv2-bar-btn-rec' : ''}`} onClick={toggleRecording} title="Record">
                <IcoRecord />
                <span>Record</span>
              </button>
            )}
            {can(user, 'transfer') && (
            <button className={`dv2-bar-btn ${showTransfer ? 'dv2-bar-btn-active' : ''}`} onClick={() => setShowTransfer(s => !s)} title="Transfer">
              <IcoTransfer />
              <span>Transfer</span>
            </button>
            )}
            <button className="dv2-bar-end-btn" onClick={hangup}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.69 19a19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-2.26-8.13A2 2 0 0 1 5.06 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91"/><line x1="23" y1="1" x2="1" y2="23"/></svg>
              End Call
            </button>
          </div>
        </div>
      )}

      {/* ═══ Add Contact modal (reuse existing logic) ═══════════════════════ */}
      {showAddContact && (
        <div className="dv2-modal-overlay" onClick={() => setShowAddContact(false)}>
          <div className="dv2-modal" onClick={e => e.stopPropagation()}>
            <div className="dv2-modal-header">
              <span>{editingContactId ? 'Edit Contact' : 'New Contact'}</span>
              <button className="dv2-close-btn" onClick={() => { setShowAddContact(false); setEditingContactId(null); }}>✕</button>
            </div>
            <div className="dv2-modal-body">
              {[
                ['Name', newContactName, setNewContactName],
                ['Phone Number', newContactNumber, setNewContactNumber],
                ['Email', newContactEmail, setNewContactEmail],
                ['Company', newContactCompany, setNewContactCompany],
                ['Notes', newContactNotes, setNewContactNotes],
              ].map(([label, val, setter]) => (
                <div key={label} className="dv2-modal-field">
                  <label className="dv2-modal-label">{label}</label>
                  <input className="dv2-modal-input" value={val} onChange={e => setter(e.target.value)} placeholder={label} />
                </div>
              ))}
              <div className="dv2-modal-actions">
                <button className="dv2-btn-secondary" onClick={() => setShowAddContact(false)}>Cancel</button>
                <button className="dv2-btn-call" style={{minWidth:100}} onClick={saveContact}>
                  {editingContactId ? 'Save Changes' : 'Add Contact'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conference Room Picker Modal */}
      {showConfModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.75)',display:'flex',
          alignItems:'center',justifyContent:'center',zIndex:9999}}
          onClick={e=>{if(e.target===e.currentTarget)setShowConfModal(false);}}>
          <div style={{background:'#1e293b',border:'1px solid #334155',borderRadius:16,
            padding:24,width:380,maxHeight:'80vh',overflowY:'auto'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
              <h3 style={{margin:0,color:'#f1f5f9',fontSize:17,fontWeight:700}}>🎙 Conference Rooms</h3>
              <button onClick={()=>setShowConfModal(false)}
                style={{background:'none',border:'none',color:'#64748b',cursor:'pointer',fontSize:18}}>✕</button>
            </div>
            {confRooms.length === 0 ? (
              <p style={{color:'#64748b',textAlign:'center',padding:'20px 0'}}>No active conference rooms found</p>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                {confRooms.map(r => (
                  <div key={r.id}
                    onClick={()=>{setNumber(r.room_number);setShowConfModal(false);}}
                    style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                      background:'#0f172a',border:'1px solid #334155',borderRadius:10,
                      padding:'12px 14px',cursor:'pointer',transition:'border-color .15s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='#C9A84C'}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='#334155'}>
                    <div>
                      <div style={{fontSize:15,fontWeight:700,color:'#f1f5f9'}}>{r.room_name}</div>
                      <div style={{fontSize:11.5,color:'#64748b',marginTop:2}}>
                        {r.require_pin ? '🔒 PIN required · ' : '🔓 Open · '}
                        {r.bridge_profile === 'ce_bridge' ? 'Admin-hosted' : 'Always open'} · Max {r.max_members}
                      </div>
                    </div>
                    <div style={{background:'#C9A84C',color:'#0f172a',borderRadius:8,
                      padding:'5px 14px',fontSize:18,fontWeight:800,flexShrink:0}}>{r.room_number}</div>
                  </div>
                ))}
              </div>
            )}
            <p style={{fontSize:11.5,color:'#475569',marginTop:16,marginBottom:0,textAlign:'center'}}>
              Click a room to pre-fill the number, then press Call
            </p>
          </div>
        </div>
      )}

      {/* CRM panel — fixed slide-in overlay so it's always visible over the dialer */}
      {crmPhone && (callStats.state === 'connected' || incoming !== null) && (
        <div className="dv2-crm-overlay">
          <div className="dv2-crm-backdrop" onClick={() => { setCrmCustomer(null); setCrmPhone(null); }} title="Click to close" />
          <div className="dv2-crm-panel">
            <CrmSidePanel phone={crmPhone} customer={crmCustomer} callUuid={crmCallUuid} onClose={() => { setCrmCustomer(null); setCrmPhone(null); }} />
          </div>
        </div>
      )}
    </div>
  );
}